import { action, computed, makeObservable } from 'mobx'

import DesktopCalendarStore from '~/client/src/desktop/components/DesktopCalendar/DesktopCalendar.store'
import DesktopCalendarColumn from '~/client/src/desktop/components/DesktopCalendar/DesktopCalendarColumn'
import DesktopCalendarTotalBarItem from '~/client/src/desktop/components/DesktopCalendar/DesktopCalendarTotalBarItem'
import { TwoMonthsDatePickerMode } from '~/client/src/desktop/components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import CalendarPlaceholderStore from '~/client/src/shared/components/CalendarDayView/components/CalendarPlaceholder.store'
import CalendarEventLabelType from '~/client/src/shared/enums/CalendarEventLabelType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CalendarEvent, {
  CalendarEventEntityType,
  ICalendarDates,
} from '~/client/src/shared/models/CalendarEvent'
import LocationAttributeBase from '~/client/src/shared/models/LocationObjects/LocationAttributeBase'
import { SharedInfo } from '~/client/src/shared/models/SharedInfo'
import Workflow from '~/client/src/shared/models/Workflow'
import { ICollectionItem } from '~/client/src/shared/stores/domain/Calendars.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { DEFAULT_TIME_INTERVAL } from '~/client/src/shared/stores/ui/CalendarView.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import EventStyleCssAdapter, {
  getSharedEventStyles,
} from '~/client/src/shared/utils/EventStyleCssAdapter'
import { isLogisticWorkflow } from '~/client/src/shared/utils/logisticsHelper'
import {
  EMPTY_CLASS_NAME,
  HIGHLIGHTED_EVENT_CLASS_NAME,
} from '~/client/src/shared/utils/usefulStrings'

import LogisticsStore from '../Logistics/Logistics.store'
import LogisticsListStore from '../LogisticsList/LogisticsList.store'

import Colors from '~/client/src/shared/theme.module.scss'

const MAX_DAYS_DIFFERENCE = 31

export default class DesktopLogisticsCalendarStore extends DesktopCalendarStore {
  public readonly eventEntityType = CalendarEventEntityType.Form

  public constructor(
    private readonly logisticsStore: LogisticsStore,
    private readonly logisticsListStore: LogisticsListStore,
    protected readonly calendarPlaceholderStore: CalendarPlaceholderStore,
    protected readonly projectDateStore: ProjectDateStore,
    protected readonly companiesStore: CompaniesStore,
    protected readonly workflowTypesStore: WorkflowTypesStore,
    protected readonly locationAttributesStore: LocationAttributesStore,
    protected readonly sharedSitePermitStore: SharedSitePermitStore,
  ) {
    super(calendarPlaceholderStore, projectDateStore, companiesStore)
    this.setCollection()
    makeObservable(this)
  }

  public get startDate(): Date {
    return this.logisticsStore.startDate
  }

  public get endDate(): Date {
    return this.logisticsStore.endDate
  }

  public get calendarEventDates(): ICalendarDates {
    return {
      startDate: this.editableEventStartDate,
      endDate: this.editableEventEndDate,
    }
  }

  @computed
  public get events(): CalendarEvent[] {
    const displayedPermitId = this.logisticsStore.displayedWorkflow?.id

    const sharedSitePermitsCalendarEvents = this._filteredSharedCollection.map(
      sharedPermit => {
        const permit = Workflow.fromSharedPermitDto(sharedPermit)

        const calendarEvent = CalendarEvent.createEvent({
          startDate: new Date(sharedPermit.startDate),
          endDate: new Date(sharedPermit.endDate),
          entityType: this.eventEntityType,
          labelType: CalendarEventLabelType.Default,
          dataId: sharedPermit.id,
          projectDateStore: this.projectDateStore,
          styles: getSharedEventStyles(),
          className: EMPTY_CLASS_NAME,
          label: `${Localization.translator.shared} ${sharedPermit.summary}`,
          data: permit,
          isActive: false,
          isAllDay: sharedPermit.isAllDay,
          isShared: true,
          sharedInfo: new SharedInfo(
            sharedPermit.responsible,
            sharedPermit.companyName,
            sharedPermit.projectUrl,
            [],
            [],
            sharedPermit.locationName,
            sharedPermit.locationUrl,
            sharedPermit.projectFromId,
          ),
        })
        return calendarEvent
      },
    )

    const permitsCalendarEvents = this._filteredCollection
      .map(permit => {
        const location =
          this.locationAttributesStore.getLocationBySiteLocationObj(
            permit.locations[0],
          )

        const eventStyle = permit.getEventStyleByColor(
          location?.color || Colors.primary20,
        )
        const cssAdapter = new EventStyleCssAdapter(eventStyle)
        const isEventHighlighted = displayedPermitId === permit.id

        return CalendarEvent.createEvent({
          startDate: new Date(permit.startDate),
          endDate: new Date(permit.endDate),
          dataId: permit.id,
          projectDateStore: this.projectDateStore,
          entityType: this.eventEntityType,
          labelType: CalendarEventLabelType.Default,
          styles: cssAdapter.getStyles(),
          className: isEventHighlighted
            ? HIGHLIGHTED_EVENT_CLASS_NAME
            : EMPTY_CLASS_NAME,
          data: permit,
          label: permit.getCaption(this.workflowTypesStore),
          isActive: isEventHighlighted,
          isAllDay: permit.isAllDay,
        })
      })
      .concat(sharedSitePermitsCalendarEvents)
    return permitsCalendarEvents
  }

  @computed
  public get calendarColumns(): DesktopCalendarColumn[] {
    const { startDate, endDate, selectedDatePickerMode } = this.logisticsStore

    switch (selectedDatePickerMode) {
      case TwoMonthsDatePickerMode.ONE_DAY_DEFAULT_TODAY:
      case TwoMonthsDatePickerMode.ONE_DAY:
      case TwoMonthsDatePickerMode.WEEK:
      case TwoMonthsDatePickerMode.FIXED_WEEK:
      case TwoMonthsDatePickerMode.MONTH:
        const validatedEndDate = this.getValidatedEndDate(endDate, startDate)

        if (!validatedEndDate || startDate >= validatedEndDate) {
          return []
        }

        const visibleDays = this.projectDateStore.getDaysInInterval(
          startDate,
          validatedEndDate,
        )

        return visibleDays.map(date =>
          DesktopCalendarColumn.createDateColumn(date, this.projectDateStore),
        )
      default:
        return []
    }
  }

  private getValidatedEndDate(
    requestedEndDate: Date,
    requestedStartDate: Date,
  ): Date {
    if (!requestedEndDate || isNaN(requestedEndDate.getTime())) {
      return null
    }

    if (
      this.projectDateStore.getDaysCountInInterval(
        requestedStartDate,
        requestedEndDate,
      ) > MAX_DAYS_DIFFERENCE
    ) {
      return this.projectDateStore.addDays(
        requestedStartDate,
        MAX_DAYS_DIFFERENCE - 1,
      )
    }

    return requestedEndDate
  }

  public get timeInterval(): number {
    return DEFAULT_TIME_INTERVAL
  }

  public get filteredAttributes(): LocationAttributeBase[] {
    return []
  }

  protected get filteredCollection(): ICollectionItem[] {
    return []
  }

  // take advantage of list filters rather than implement individual ones for this particular view
  @computed
  private get _filteredCollection() {
    return this.logisticsListStore.filteredCollection
      .filter(isLogisticWorkflow)
      .filter(lItem => !lItem.isShared)
      .map(lItem => lItem.original)
      .filter(f => !!f)
  }

  @computed
  private get _filteredSharedCollection() {
    return this.logisticsListStore.filteredCollection
      .map(lItem => this.sharedSitePermitStore.getById(lItem.entityId))
      .filter(f => !!f)
  }

  protected getTotalBarItems(): DesktopCalendarTotalBarItem[] {
    return []
  }

  public setShowingOfAllZoneColumns = () => {
    return
  }

  @action.bound
  public setCalendarEventDates(startDate: Date, endDate: Date) {
    this.editableEventStartDate = startDate
    this.editableEventEndDate = endDate
  }

  @action.bound
  public onEventDateChange(
    event: CalendarEvent,
    startDate: Date,
    endDate: Date,
  ) {
    this.setCalendarEventDates(startDate, endDate)

    if (event.isNew) {
      const newEvent = this.getNewEvent(this.eventEntityType)

      event.dateInterval.startDate = newEvent.startDate
      event.dateInterval.endDate = newEvent.endDate
      event.isAllDay = newEvent.isAllDay

      this.calendarPlaceholderStore.setEventPlaceholder(newEvent)
    } else {
      this.logisticsStore.openDateMoveConfirmModal()

      if (this.calendarPlaceholderStore.eventPlaceholder) {
        this.calendarPlaceholderStore.resetEventPlaceholder()
      }
    }
  }

  @action.bound
  public onNewEventCreate(startDate?: Date, endDate?: Date) {
    this.setCalendarEventDates(startDate, endDate)

    this.logisticsStore.hidePermitDialog()
    this.logisticsStore.openFormTypeSelectionForm()
  }
}
